import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE } from "../lib/api";
import { statusBadgeClass, statusLabel, formatApiError } from "../lib/format";
import { Card, Loading } from "../components/UI";
import { UserPlus, Mail, Phone, ChevronRight, X, Send, FileText, Eye } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function Tenants() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const reload = async () => {
    const r = await api.get("/tenants");
    setTenants(r.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    try {
      const { data } = await api.post("/auth/invite-tenant", {
        full_name: form.get("full_name"),
        email: form.get("email"),
        phone: form.get("phone"),
      });
      toast.success(`Tenant invited! Temporary password: ${data.temp_password}`);
      setInviteOpen(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const resendInvite = async (e, t) => {
    e.stopPropagation();
    if (!t.user_id) { toast.error("This tenant has no login account yet"); return; }
    if (!confirm(`Resend the invite email to ${t.full_name}? A new temporary password will replace the old one.`)) return;
    try {
      const { data } = await api.post(`/users/${t.user_id}/resend-invite`);
      toast.success(`Invite re-sent. Temp password: ${data.temp_password}`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <Loading/>;
  const canInvite = ["admin","manager"].includes(user.role);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">Tenants</h1>
          <p className="text-[#5E7166] text-sm">{tenants.length} tenant records.</p>
        </div>
        {canInvite && <button onClick={() => setInviteOpen(true)} className="btn-gold" data-testid="invite-tenant-btn"><UserPlus size={16}/> Invite Tenant</button>}
      </div>

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="modav-table">
            <thead><tr><th>Name</th><th>Unit</th><th>Contact</th><th>Use</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {tenants.map(t => (
                <tr key={t.id} className="cursor-pointer" onClick={() => setSelectedTenant(t)} data-testid={`tenant-row-${t.id}`}>
                  <td><div className="font-semibold">{t.full_name}</div><div className="text-xs text-[#5E7166]">{t.occupant_count} occupant(s)</div></td>
                  <td>{t.unit ? <><div className="font-semibold">{t.unit.unit_name}</div><div className="text-xs text-[#5E7166]">{t.unit.unit_type_name}</div></> : <span className="text-[#5E7166]">—</span>}</td>
                  <td><div className="flex flex-col text-xs gap-0.5"><span className="flex items-center gap-1"><Mail size={11}/>{t.email}</span><span className="flex items-center gap-1"><Phone size={11}/>{t.phone}</span></div></td>
                  <td className="text-xs">{t.use_of_apartment || "—"}</td>
                  <td><span className={statusBadgeClass(t.status)}>{statusLabel(t.status)}</span></td>
                  <td>
                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                      {canInvite && t.user_id && <button onClick={(e) => resendInvite(e, t)} title="Resend invite email" className="text-[#0E5C3A] hover:bg-[#E8F5EE] p-2 rounded" data-testid={`resend-invite-tenant-${t.id}`}><Send size={14}/></button>}
                      <ChevronRight size={16} className="text-[#5E7166]"/>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {inviteOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-md" data-testid="invite-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl font-bold">Invite New Tenant</h3>
              <button onClick={() => setInviteOpen(false)} className="text-[#5E7166]"><X size={20}/></button>
            </div>
            <form onSubmit={invite} className="space-y-3">
              <input name="full_name" required placeholder="Full name" className="input-modav" data-testid="invite-name"/>
              <input name="email" type="email" required placeholder="Email" className="input-modav" data-testid="invite-email"/>
              <input name="phone" placeholder="Phone (e.g. +2348012345678)" className="input-modav" data-testid="invite-phone"/>
              <button type="submit" className="btn-gold w-full justify-center" data-testid="invite-submit">Send Invitation</button>
              <p className="text-xs text-[#5E7166]">A temporary password will be generated. Share login URL with tenant.</p>
            </form>
          </div>
        </div>
      )}

      {selectedTenant && <TenantDrawer tenant={selectedTenant} onClose={() => setSelectedTenant(null)} onUpdated={reload}/>}
    </div>
  );
}

function TenantDrawer({ tenant, onClose, onUpdated }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [details, setDetails] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [allDocs, setAllDocs] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [busyAction, setBusyAction] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);

  const TENANT_STATUSES = [
    "invited", "pending_onboarding", "awaiting_approval", "approved",
    "active", "renewal_due", "overdue", "off_lease", "notice_served", "moved_out", "blacklisted",
  ];

  const reload = async () => {
    const [t, c, d] = await Promise.all([
      api.get(`/tenants/${tenant.id}`),
      api.get(`/tenants/${tenant.id}/document-checklist`).catch(() => ({ data: null })),
      api.get(`/tenants/${tenant.id}/all-documents`).catch(() => ({ data: null })),
    ]);
    setDetails(t.data);
    setChecklist(c.data);
    setAllDocs(d.data);
  };
  useEffect(() => { reload(); }, [tenant.id]);

  const updateStatus = async (newStatus) => {
    setSavingStatus(true);
    try {
      await api.patch(`/tenants/${tenant.id}`, { status: newStatus });
      toast.success("Status updated");
      await reload();
      onUpdated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingStatus(false); }
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    const payload = {
      full_name: form.get("full_name"),
      email: form.get("email"),
      phone: form.get("phone"),
      tenant_address: form.get("tenant_address"),
      occupation_or_business: form.get("occupation_or_business"),
      use_of_apartment: form.get("use_of_apartment"),
    };
    setBusyAction(true);
    try {
      await api.patch(`/tenants/${tenant.id}`, payload);
      toast.success("Tenant updated");
      setEditingProfile(false);
      await reload();
      onUpdated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyAction(false); }
  };

  const setActiveFlag = async (active) => {
    const verb = active ? "Reactivate" : "Deactivate";
    if (!confirm(`${verb} ${tenant.full_name}? ${active ? "They will regain access." : "Their access will be revoked immediately."}`)) return;
    setBusyAction(true);
    try {
      await api.patch(`/tenants/${tenant.id}/status`, { status: active ? "active" : "inactive" });
      toast.success(`Tenant ${active ? "reactivated" : "deactivated"}`);
      await reload();
      onUpdated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyAction(false); }
  };

  const hardDelete = async () => {
    const phrase = `DELETE ${tenant.full_name}`;
    const input = prompt(`This permanently deletes ${tenant.full_name}'s record, frees their unit, and removes guarantor/document/maintenance data. Type "${phrase}" to confirm.`);
    if (input !== phrase) { if (input !== null) toast.error("Confirmation phrase did not match — cancelled."); return; }
    setBusyAction(true);
    try {
      await api.delete(`/tenants/${tenant.id}`);
      toast.success("Tenant deleted");
      onUpdated?.();
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyAction(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end">
      <div className="bg-white w-full max-w-lg h-full overflow-y-auto p-6" data-testid="tenant-drawer">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl font-bold">{tenant.full_name}</h3>
          <button onClick={onClose} className="text-[#5E7166]"><X size={20}/></button>
        </div>
        {!details ? <Loading/> : (
          <div className="space-y-4 text-sm">
            <div className="card-modav p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227] mb-2">Lease Tenure</div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="text-2xl font-serif font-bold text-[#0E5C3A]" data-testid="tenant-years-on-lease">{details.years_on_lease ?? 0}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5E7166]">Years on lease</div>
                </div>
                <div>
                  <div className="text-2xl font-serif font-bold text-[#0E5C3A]">{details.lease_history_count || 0}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#5E7166]">Total leases</div>
                </div>
                <div>
                  <div className={`text-xs font-bold mt-1 ${details.current_lease ? "text-[#0E5C3A]" : "text-[#C92A2A]"}`}>
                    {details.current_lease ? statusLabel(details.current_lease.lease_status) : "Off lease"}
                  </div>
                  {details.current_lease?.lease_end_date && (
                    <div className="text-[10px] text-[#5E7166] mt-1">Ends {details.current_lease.lease_end_date}</div>
                  )}
                </div>
              </div>
            </div>

            <div className="card-modav p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227] mb-2">Lifecycle Status</div>
              <select value={details.status || "active"} onChange={(e) => updateStatus(e.target.value)} disabled={savingStatus} className="input-modav" data-testid="tenant-status-select">
                {TENANT_STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
              </select>
            </div>

            <div className="card-modav p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227] mb-2">Document Checklist</div>
              {!checklist ? <div className="text-[#5E7166]">Loading…</div> : (
                <ul className="space-y-1.5">
                  {checklist.checklist.map(c => (
                    <li key={c.code} className="flex items-center justify-between text-sm">
                      <span className={c.present ? "" : "text-[#C92A2A]"}>{c.label}</span>
                      <span className={c.present ? "badge badge-green" : "badge badge-red"}>{c.present ? "✓" : "Missing"}</span>
                    </li>
                  ))}
                  {checklist.complete && <li className="text-xs text-[#0E5C3A] font-semibold mt-2">All documents on file ✓</li>}
                </ul>
              )}
            </div>

            <div className="card-modav p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227] mb-2">Statements</div>
              <button
                onClick={async () => {
                  try {
                    const res = await api.get(`/tenants/${tenant.id}/payment-history.pdf`, { responseType: "blob" });
                    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `payment-history-${tenant.full_name.replace(/ /g,"_")}-${new Date().toISOString().slice(0,10)}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    URL.revokeObjectURL(url);
                    toast.success("Payment history downloaded");
                  } catch (err) { toast.error(formatApiError(err) || "Download failed"); }
                }}
                className="btn-outline-emerald text-xs inline-flex items-center gap-1.5 w-full justify-center"
                data-testid="download-payment-history-pdf"
              >
                <FileText size={14}/> Download Payment History PDF
              </button>
            </div>

            <div className="card-modav p-4" data-testid="all-documents-card">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227]">All Uploaded Documents</div>
                <span className="text-[10px] text-[#5E7166]">{allDocs?.count || 0} items</span>
              </div>
              {!allDocs ? <div className="text-xs text-[#5E7166]">Loading…</div> :
                allDocs.count === 0 ? <div className="text-xs text-[#5E7166] italic">No documents uploaded yet.</div> :
                <div className="space-y-3">
                  {Object.entries(allDocs.by_category).map(([cat, items]) => (
                    <div key={cat}>
                      <div className="text-[10px] uppercase tracking-wider text-[#5E7166] font-bold mb-1">{cat.replace("_"," ")}</div>
                      <ul className="space-y-1">
                        {items.map((it, i) => (
                          <li key={i} className="text-sm flex items-center justify-between gap-2 group">
                            <span className="truncate flex-1 text-[#1A2E24]" title={it.label}>{it.label}</span>
                            <a
                              href={`${API_BASE}/files/view?path=${encodeURIComponent(it.path)}`}
                              target="_blank"
                              rel="noreferrer"
                              className="btn-outline-emerald text-[10px] shrink-0 inline-flex items-center gap-1 opacity-70 group-hover:opacity-100"
                              data-testid={`doc-view-${cat}-${i}`}
                            >
                              <Eye size={11}/> View
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>}
            </div>

            <div className="card-modav p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227]">Profile</div>
                {isAdmin && (
                  <button onClick={() => setEditingProfile(v => !v)} className="text-xs text-[#0E5C3A] hover:underline" data-testid="tenant-edit-profile-toggle">
                    {editingProfile ? "Cancel" : "Edit"}
                  </button>
                )}
              </div>
              {!editingProfile ? (
                <div className="grid grid-cols-2 gap-y-2">
                  <div className="text-[#5E7166]">Email</div><div>{details.email}</div>
                  <div className="text-[#5E7166]">Phone</div><div>{details.phone}</div>
                  <div className="text-[#5E7166]">Address</div><div>{details.tenant_address || "—"}</div>
                  <div className="text-[#5E7166]">Occupation</div><div>{details.occupation_or_business || "—"}</div>
                  <div className="text-[#5E7166]">Use</div><div>{details.use_of_apartment || "—"}</div>
                  <div className="text-[#5E7166]">Occupants</div><div>{details.occupant_count || 0}</div>
                  <div className="text-[#5E7166]">Emergency</div><div>{details.emergency_contact_name ? `${details.emergency_contact_name} (${details.emergency_contact_phone || ""})` : "—"}</div>
                </div>
              ) : (
                <form onSubmit={saveProfile} className="space-y-2 text-sm" data-testid="tenant-edit-form">
                  <label className="block"><span className="text-[10px] uppercase tracking-wider text-[#5E7166]">Full name</span>
                    <input name="full_name" defaultValue={details.full_name} required className="input-modav mt-1" data-testid="edit-full-name"/></label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block"><span className="text-[10px] uppercase tracking-wider text-[#5E7166]">Email</span>
                      <input name="email" type="email" defaultValue={details.email} required className="input-modav mt-1" data-testid="edit-email"/></label>
                    <label className="block"><span className="text-[10px] uppercase tracking-wider text-[#5E7166]">Phone</span>
                      <input name="phone" defaultValue={details.phone} className="input-modav mt-1" data-testid="edit-phone"/></label>
                  </div>
                  <label className="block"><span className="text-[10px] uppercase tracking-wider text-[#5E7166]">Address</span>
                    <input name="tenant_address" defaultValue={details.tenant_address || ""} className="input-modav mt-1" data-testid="edit-address"/></label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block"><span className="text-[10px] uppercase tracking-wider text-[#5E7166]">Occupation</span>
                      <input name="occupation_or_business" defaultValue={details.occupation_or_business || ""} className="input-modav mt-1" data-testid="edit-occupation"/></label>
                    <label className="block"><span className="text-[10px] uppercase tracking-wider text-[#5E7166]">Use of apartment</span>
                      <input name="use_of_apartment" defaultValue={details.use_of_apartment || ""} className="input-modav mt-1" data-testid="edit-use"/></label>
                  </div>
                  <button type="submit" disabled={busyAction} className="btn-primary text-xs w-full justify-center mt-2" data-testid="tenant-save-profile">Save changes</button>
                </form>
              )}
            </div>
            <div className="card-modav p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227] mb-2">Unit</div>
              {details.unit ? (
                <div className="grid grid-cols-2 gap-y-2">
                  <div className="text-[#5E7166]">Unit</div><div>{details.unit.unit_name}</div>
                  <div className="text-[#5E7166]">Type</div><div>{details.unit.unit_type_name}</div>
                  <div className="text-[#5E7166]">Number</div><div>{details.unit.unit_number}</div>
                </div>
              ) : <div className="text-[#5E7166]">No unit assigned</div>}
            </div>
            <div className="card-modav p-4">
              <div className="text-xs font-bold uppercase tracking-wider text-[#C9A227] mb-2">Guarantor</div>
              {details.guarantor ? (
                <div className="grid grid-cols-2 gap-y-2">
                  <div className="text-[#5E7166]">Name</div><div>{details.guarantor.guarantor_name}</div>
                  <div className="text-[#5E7166]">Phone</div><div>{details.guarantor.guarantor_phone}</div>
                  <div className="text-[#5E7166]">Address</div><div>{details.guarantor.guarantor_address}</div>
                  <div className="text-[#5E7166]">Occupation</div><div>{details.guarantor.guarantor_occupation}</div>
                </div>
              ) : <div className="text-[#5E7166]">No guarantor on file</div>}
            </div>

            {isAdmin && (
              <div className="card-modav p-4 border-l-4 border-[#C92A2A]">
                <div className="text-xs font-bold uppercase tracking-wider text-[#C92A2A] mb-2">Admin actions</div>
                <p className="text-xs text-[#5E7166] mb-3">
                  Deactivating revokes the tenant's login immediately. Deletion is permanent — payments and signed leases are archived for audit but the tenant profile, documents, guarantor and maintenance history are removed.
                </p>
                <div className="flex flex-wrap gap-2">
                  {details.status === "inactive" ? (
                    <button onClick={() => setActiveFlag(true)} disabled={busyAction} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="tenant-reactivate">
                      Reactivate tenant
                    </button>
                  ) : (
                    <button onClick={() => setActiveFlag(false)} disabled={busyAction} className="btn-ghost inline-flex items-center gap-2 text-sm border border-[#C9A227] text-[#7A6810]" data-testid="tenant-deactivate">
                      Deactivate access
                    </button>
                  )}
                  <button onClick={hardDelete} disabled={busyAction} className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-[#C92A2A] text-white hover:bg-[#A21F1F] disabled:opacity-50" data-testid="tenant-delete">
                    Delete tenant permanently
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
